import { mat3, vec2, vec3 } from 'gl-matrix';
import { DcmJsDatasetWrapper } from './dcmJsWrapper';
import {
    DicomDose,
    DicomDvh,
    strToDoseSummationType,
    strToDoseType,
    strToDoseUnit,
    strToDvhType,
    strToVolumeUnits,
} from './dicomDose';
import { DicomDataset } from './engine/dataset';
import { DicomFile } from './engine/reader';
import * as tags from './engine/tags';
import { multiValuedStringText } from './multiValuedString';

/**
 * Builds a {@link DicomDose} from the typed dataset model.
 *
 * Reads the same attributes as the equivalent in `dicomProcessor` that takes
 * dcmjs's keyword-addressed shape, without the projection in between — a dose
 * grid is the largest object the application handles, and projecting one
 * copies its whole pixel buffer out to satisfy callers expecting an
 * ArrayBuffer.
 */

/** A dose attribute whose value the application cannot work with. */
export class UnsupportedDoseError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'UnsupportedDoseError';
    }
}

function requireSupported(condition: boolean, message: string): void {
    if (!condition) {
        throw new UnsupportedDoseError(message);
    }
}

/** The rotation taking dose-grid space to patient space. */
function orientationMatrix(cosines: readonly number[]): {
    matrix: mat3;
    normal: vec3;
} {
    const row = vec3.fromValues(cosines[0], cosines[1], cosines[2]);
    const column = vec3.fromValues(cosines[3], cosines[4], cosines[5]);
    const normal = vec3.create();
    vec3.cross(normal, row, column);
    return {
        matrix: mat3.fromValues(
            row[0],
            row[1],
            row[2],
            column[0],
            column[1],
            column[2],
            normal[0],
            normal[1],
            normal[2]
        ),
        normal,
    };
}

/**
 * The referenced instance UID of a sequence that references exactly one
 * object, or '' when the sequence is absent.
 *
 * The sequence is always an array in this model, so there is no single-item
 * shape to unwrap before indexing it.
 */
function referencedInstanceUid(
    dataset: DicomDataset,
    sequenceTag: number
): string {
    const [item] = dataset.sequence(sequenceTag);
    return item?.string(tags.REFERENCED_SOP_INSTANCE_UID) ?? '';
}

/**
 * Reads the DVHs the application can display.
 *
 * A DVH is skipped rather than adapted when it is not the cumulative,
 * included, Gy-valued form the viewer plots — presenting a differential or
 * error-valued histogram on the same axes would misstate the dose delivered.
 */
function readDvhs(dataset: DicomDataset): DicomDvh[] {
    const all = dataset.sequence(tags.DVH_SEQUENCE);
    const supported: DicomDvh[] = [];
    let skipped = 0;

    for (const dvh of all) {
        const [referencedRoi] = dvh.sequence(tags.DVH_REFERENCED_ROI_SEQUENCE);
        const isSupported =
            referencedRoi !== undefined &&
            dvh.sequence(tags.DVH_REFERENCED_ROI_SEQUENCE).length === 1 &&
            referencedRoi.string(tags.DVH_ROI_CONTRIBUTION_TYPE) ===
                'INCLUDED' &&
            dvh.string(tags.DOSE_UNITS) === 'GY' &&
            dvh.string(tags.DOSE_TYPE) !== 'ERROR' &&
            dvh.string(tags.DVH_TYPE) === 'CUMULATIVE';

        if (!isSupported || !referencedRoi) {
            skipped += 1;
            continue;
        }

        supported.push(
            new DicomDvh(
                strToDoseUnit(dvh.string(tags.DOSE_UNITS) as string),
                strToDoseType(dvh.string(tags.DOSE_TYPE) as string),
                strToDvhType(dvh.string(tags.DVH_TYPE) as string),
                strToVolumeUnits(dvh.string(tags.DVH_VOLUME_UNITS) as string),
                dvh.number(tags.DVH_NUMBER_OF_BINS) as number,
                dvh.number(tags.DVH_DOSE_SCALING) as number,
                [...dvh.numbers(tags.DVH_DATA)],
                referencedRoi.number(tags.REFERENCED_ROI_NUMBER) as number,
                dvh.number(tags.DVH_MINIMUM_DOSE) as number,
                dvh.number(tags.DVH_MEAN_DOSE) as number,
                dvh.number(tags.DVH_MAXIMUM_DOSE) as number
            )
        );
    }

    if (skipped > 0) {
        console.warn(`Found ${skipped} DVHs that are not supported.`);
    }
    return supported;
}

/** How far a frame offset may sit from zero and still count as the origin. */
const FIRST_OFFSET_TOLERANCE_MM = 1e-4;

/** How far consecutive frame spacings may differ and still count as uniform. */
const UNIFORM_SPACING_TOLERANCE_MM = 0.1;

/**
 * Centre-to-centre spacing of the dose grid's frames, in mm.
 *
 * The vector is validated rather than sampled, because the grid it describes
 * is uniform and every consumer treats it that way. A vector that does not
 * describe a uniform grid starting at the image position has no single spacing
 * to report, and reporting one anyway draws the dose at positions the plan
 * never assigned it — visibly plausible, and wrong.
 *
 * @throws {UnsupportedDoseError} for a vector this cannot reduce to one
 * spacing.
 */
function sliceResolution(offsets: readonly number[]): number {
    if (offsets.length < 2) {
        throw new UnsupportedDoseError(
            'GridFrameOffsetVector must have at least 2 entries to compute ' +
                `slice resolution, got ${offsets.length}.`
        );
    }
    if (Math.abs(offsets[0]) > FIRST_OFFSET_TOLERANCE_MM) {
        // PS3.3 C.8.8.3.2 allows the vector to be read as offsets from the
        // image position or as absolute coordinates. Only the first is
        // supported; reading an absolute vector as relative shifts the whole
        // grid by its first offset.
        throw new UnsupportedDoseError(
            `Unexpected grid frame offset of ${offsets[0]} for the first ` +
                'slice. Expected 0.'
        );
    }

    const spacing = Math.abs(offsets[1] - offsets[0]);
    for (let i = 1; i < offsets.length; i++) {
        const step = Math.abs(offsets[i] - offsets[i - 1]);
        if (Math.abs(step - spacing) > UNIFORM_SPACING_TOLERANCE_MM) {
            throw new UnsupportedDoseError(
                `Unexpected grid frame offset of ${step} between slices ` +
                    `${i - 1} and ${i}. Expected ${spacing}.`
            );
        }
    }
    return spacing;
}

/**
 * Reads a dose grid.
 *
 * Returns null when the object carries no placeable grid — no spacing,
 * position, orientation, dose type or dose units — which is how a file that is
 * not a usable dose has always been reported.
 *
 * @throws {UnsupportedDoseError} when the pixel encoding or the frame offsets
 * are ones the application cannot interpret. A dose read under the wrong
 * encoding, or drawn on a grid whose spacing was guessed, scales or places
 * every voxel wrongly while still producing something that looks like a dose.
 */
export function readDose(file: DicomFile): DicomDose | null {
    const dataset = file.dataset;
    const sopInstanceUid = dataset.string(tags.SOP_INSTANCE_UID) ?? '';

    // Without these there is no grid to place. Reported as "not a dose" rather
    // than as an error, matching how such a file was treated before the typed
    // reader: the alternative is a DicomDose whose transform is NaN, which
    // renders and computes without ever failing.
    if (
        dataset.numbers(tags.PIXEL_SPACING).length < 2 ||
        dataset.numbers(tags.IMAGE_POSITION_PATIENT).length < 3 ||
        dataset.numbers(tags.IMAGE_ORIENTATION_PATIENT).length < 6 ||
        dataset.string(tags.DOSE_TYPE) === undefined ||
        dataset.string(tags.DOSE_UNITS) === undefined
    ) {
        return null;
    }

    const pixelRepresentation = dataset.number(tags.PIXEL_REPRESENTATION);
    const bitsAllocated = dataset.number(tags.BITS_ALLOCATED);
    const bitsStored = dataset.number(tags.BITS_STORED);
    const highBit = dataset.number(tags.HIGH_BIT);

    requireSupported(
        pixelRepresentation === 0,
        `Unsupported PixelRepresentation '${pixelRepresentation}'. SOP UID ${sopInstanceUid}`
    );
    requireSupported(
        bitsAllocated === 16 || bitsAllocated === 32,
        `Unsupported BitsAllocated ${bitsAllocated}. SOP UID ${sopInstanceUid}`
    );
    requireSupported(
        highBit === (bitsStored ?? 0) - 1,
        `Unsupported HighBit '${highBit}'. SOP UID ${sopInstanceUid}`
    );

    const cosines = dataset.numbers(tags.IMAGE_ORIENTATION_PATIENT);
    const { matrix, normal } = orientationMatrix(cosines);

    const offsets = dataset.numbers(tags.GRID_FRAME_OFFSET_VECTOR);
    // A negative step means the frames extend opposite to the plane normal.
    // The grid is normalized to a positive step by moving the origin to the
    // last frame; the pixel data is reversed lazily on use, so the dataset
    // this was read from stays untouched and exports as it arrived.
    const reverseFrameOrder =
        offsets.length >= 2 && offsets[1] - offsets[0] < 0;

    const position = dataset.numbers(tags.IMAGE_POSITION_PATIENT);
    let imagePosition = vec3.fromValues(position[0], position[1], position[2]);
    if (reverseFrameOrder) {
        imagePosition = vec3.scaleAndAdd(
            vec3.create(),
            imagePosition,
            normal,
            offsets[offsets.length - 1]
        );
    }

    const spacing = dataset.numbers(tags.PIXEL_SPACING);

    return new DicomDose(
        sopInstanceUid,
        dataset.string(tags.FRAME_OF_REFERENCE_UID) as string,
        vec2.fromValues(spacing[0], spacing[1]),
        sliceResolution(offsets),
        imagePosition,
        matrix,
        dataset.string(tags.SERIES_DESCRIPTION) as string,
        dataset.string(tags.MANUFACTURER) as string,
        dataset.string(tags.MANUFACTURER_MODEL_NAME) as string,
        dataset.number(tags.ROWS) as number,
        dataset.number(tags.COLUMNS) as number,
        dataset.number(tags.NUMBER_OF_FRAMES) as number,
        strToDoseUnit(dataset.string(tags.DOSE_UNITS) as string),
        strToDoseType(dataset.string(tags.DOSE_TYPE) as string),
        strToDoseSummationType(
            dataset.string(tags.DOSE_SUMMATION_TYPE) as string
        ),
        dataset.string(tags.DOSE_COMMENT) as string,
        dataset.number(tags.DOSE_GRID_SCALING) as number,
        multiValuedStringText(dataset.strings(tags.SOFTWARE_VERSIONS)),
        referencedInstanceUid(dataset, tags.REFERENCED_RT_PLAN_SEQUENCE),
        referencedInstanceUid(dataset, tags.REFERENCED_STRUCTURE_SET_SEQUENCE),
        readDvhs(dataset),
        new DcmJsDatasetWrapper(file),
        reverseFrameOrder,
        dataset.string(tags.SERIES_DATE) as string,
        dataset.string(tags.SERIES_TIME) as string
    );
}
